import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Plus, Edit, Trash2, Check, X, Download, Search, MessageCircle, ShoppingCart } from 'lucide-react';
import { useConfig } from '../context/ConfigContext';
import { cotizacionesAPI, ventasAPI } from '../api/client';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const CotizacionesPage = () => {
  const navigate = useNavigate();
  const [cotizaciones, setCotizaciones] = useState([]);
  const [filtro, setFiltro] = useState('');
  const [loading, setLoading] = useState(false);
  const { formatCurrency, config } = useConfig();

  useEffect(() => {
    loadCotizaciones();
  }, []);

  const loadCotizaciones = async () => {
    setLoading(true);
    try {
      const response = await cotizacionesAPI.getAll();
      if (response.data.success) {
        setCotizaciones(response.data.data);
      }
    } catch (error) {
      console.error('Error al cargar cotizaciones:', error);
    } finally {
      setLoading(false);
    }
  };

  const crearCotizacion = () => {
    navigate('/cotizaciones/nueva');
  };

  const convertirAVenta = async (id) => {
    if (!window.confirm('💰 ¿Convertir esta cotización en venta?\n\nSe creará una nueva venta con los productos de esta cotización.')) {
      return;
    }

    try {
      // Obtener detalles completos de la cotización
      const cotizacion = await obtenerDetalleCotizacion(id);
      if (!cotizacion) {
        alert('❌ Error al obtener los detalles de la cotización');
        return;
      }

      // Verificar que tenga productos
      if (!cotizacion.items || cotizacion.items.length === 0) {
        alert('❌ La cotización no tiene productos');
        return;
      }

      // Preparar datos para la venta
      const ventaData = {
        cliente_id: cotizacion.cliente_id || null,
        detalle: cotizacion.items.map(item => {
          const cantidad = parseFloat(item.cantidad) || 1;
          const precio_unitario = parseFloat(item.precio_unitario) || 0;
          const descuento_monto = parseFloat(item.descuento) || 0;
          const subtotal = (cantidad * precio_unitario) - descuento_monto;
          
          return {
            producto_id: parseInt(item.producto_id) || 0,
            cantidad: cantidad,
            precio_unitario: precio_unitario,
            descuento: 0, // Descuento porcentaje
            descuento_monto: descuento_monto, // Descuento en dinero
            subtotal: subtotal
          };
        }),
        subtotal: parseFloat(cotizacion.total) || 0,
        descuento: 0,
        total: parseFloat(cotizacion.total) || 0,
        metodo_pago: 'Efectivo',
        forma_pago: 'efectivo'
      };

      // Debug: Ver datos que se envían
      console.log('Datos de venta a enviar:', ventaData);
      console.log('Items de cotización:', cotizacion.items);

      // Validar que todos los productos tengan precio_unitario
      const itemsInvalidos = ventaData.detalle.filter(item => !item.precio_unitario || item.precio_unitario === 0);
      if (itemsInvalidos.length > 0) {
        console.error('Items sin precio:', itemsInvalidos);
        console.error('Cotización completa:', cotizacion);
        alert(`❌ Error: Algunos productos no tienen precio válido.\n\nProductos afectados: ${itemsInvalidos.length}\n\nPor favor, edita la cotización y asegúrate de que todos los productos tengan precio.`);
        return;
      }
      
      // Validar que todos los productos tengan producto_id válido
      const itemsSinProducto = ventaData.detalle.filter(item => !item.producto_id || item.producto_id === 0);
      if (itemsSinProducto.length > 0) {
        console.error('Items sin producto_id:', itemsSinProducto);
        alert(`❌ Error: Algunos items no tienen producto_id válido.`);
        return;
      }

      // Crear la venta
      const response = await ventasAPI.create(ventaData);
      
      if (response.data.success) {
        const folio = response.data.data?.folio || 'N/A';
        
        // Marcar cotización como convertida (vencida)
        try {
          await cotizacionesAPI.updateEstado(id, 'vencida');
        } catch (error) {
          console.error('Error al actualizar estado de cotización:', error);
          // Continuar aunque falle el update
        }
        
        alert(`✅ Venta ${folio} creada exitosamente\n\nLa cotización se ha convertido en venta.`);
        
        // Preguntar si desea ir a la venta
        if (window.confirm('¿Desea ver las ventas?')) {
          navigate('/ventas');
        } else {
          loadCotizaciones(); // Recargar lista
        }
      } else {
        alert('❌ Error al crear la venta: ' + (response.data.message || 'Error desconocido'));
      }
    } catch (error) {
      console.error('Error al convertir cotización a venta:', error);
      const errorMsg = error.response?.data?.message || error.message || 'Error de conexión';
      alert(`❌ Error al convertir a venta: ${errorMsg}`);
    }
  };

  const aprobarCotizacion = async (id) => {
    if (window.confirm('✅ ¿Aprobar esta cotización?')) {
      try {
        const response = await cotizacionesAPI.updateEstado(id, 'aprobada');
        if (response.data.success) {
          alert('✅ Cotización aprobada exitosamente');
          loadCotizaciones(); // Recargar lista
        } else {
          alert('❌ Error: ' + (response.data.message || 'Error al aprobar'));
        }
      } catch (error) {
        console.error('Error al aprobar cotización:', error);
        alert('❌ Error al aprobar la cotización');
      }
    }
  };

  const rechazarCotizacion = async (id) => {
    if (window.confirm('❌ ¿Rechazar esta cotización?')) {
      try {
        const response = await cotizacionesAPI.updateEstado(id, 'rechazada');
        if (response.data.success) {
          alert('✅ Cotización rechazada');
          loadCotizaciones(); // Recargar lista
        } else {
          alert('❌ Error: ' + (response.data.message || 'Error al rechazar'));
        }
      } catch (error) {
        console.error('Error al rechazar cotización:', error);
        alert('❌ Error al rechazar la cotización');
      }
    }
  };

  const eliminarCotizacion = async (id) => {
    if (window.confirm('⚠️ ¿Está seguro de eliminar esta cotización?\n\nEsta acción no se puede deshacer.')) {
      try {
        const response = await cotizacionesAPI.delete(id);
        if (response.data.success) {
          setCotizaciones(cotizaciones.filter(c => c.id !== id));
          alert('✅ Cotización eliminada correctamente');
        } else {
          alert('❌ Error: ' + (response.data.message || 'Error al eliminar'));
        }
      } catch (error) {
        console.error('Error al eliminar cotización:', error);
        alert('❌ Error al eliminar la cotización');
      }
    }
  };

  const obtenerDetalleCotizacion = async (id) => {
    try {
      const response = await cotizacionesAPI.getById(id);
      if (response.data.success) {
        return response.data.data;
      }
      return null;
    } catch (error) {
      console.error('Error al obtener detalle:', error);
      return null;
    }
  };

  const compartirWhatsApp = async (id) => {
    try {
      const cotizacion = await obtenerDetalleCotizacion(id);
      if (!cotizacion) {
        alert('❌ Error al obtener los detalles de la cotización');
        return;
      }

      const nombreNegocio = config?.nombre_negocio || 'Nuestro Negocio';
      const nombreCliente = cotizacion.cliente_nombre || 'Cliente';
      const telefonoNegocio = config?.telefono || '';
      
      let mensaje = `*${nombreNegocio.toUpperCase()}*\n`;
      if (telefonoNegocio) mensaje += `📞 ${telefonoNegocio}\n`;
      mensaje += `\n📋 *COTIZACIÓN ${cotizacion.folio}*\n`;
      mensaje += `━━━━━━━━━━━━━━━━\n\n`;
      mensaje += `Hola *${nombreCliente}*, aquí está tu cotización:\n\n`;
      
      if (cotizacion.items && cotizacion.items.length > 0) {
        cotizacion.items.forEach((item, index) => {
          const subtotal = (item.precio_unitario * item.cantidad) - (item.descuento || 0);
          const nombre = item.producto_nombre || item.nombre || 'Producto';
          
          mensaje += `${index + 1}. *${nombre}*\n`;
          mensaje += `   ${item.cantidad} x ${formatCurrency(item.precio_unitario)}`;
          if (item.descuento > 0) {
            mensaje += ` - ${formatCurrency(item.descuento)}`;
          }
          mensaje += ` = ${formatCurrency(subtotal)}\n\n`;
        });
      }

      mensaje += `━━━━━━━━━━━━━━━━\n`;
      mensaje += `💰 *TOTAL: ${formatCurrency(cotizacion.total)}*\n`;
      mensaje += `\n📅 Vigencia: ${new Date(cotizacion.vigencia).toLocaleDateString('es-GT', { year: 'numeric', month: 'long', day: 'numeric' })}\n`;
      mensaje += `\n¡Gracias por tu preferencia! 🙏`;
      
      const numero = cotizacion.cliente_telefono ? cotizacion.cliente_telefono.replace(/[^0-9]/g, '') : '';
      const url = numero 
        ? `https://wa.me/${numero}?text=${encodeURIComponent(mensaje)}`
        : `https://wa.me/?text=${encodeURIComponent(mensaje)}`;
      
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error al compartir por WhatsApp:', error);
      alert('❌ Error al generar el mensaje de WhatsApp');
    }
  };

  const descargarPDF = async (id) => {
    try {
      const cotizacion = await obtenerDetalleCotizacion(id);
      if (!cotizacion) {
        alert('❌ Error al obtener los detalles de la cotización');
        return;
      }

      const doc = new jsPDF();
      const nombreNegocio = config?.nombre_negocio || 'Mi Negocio';
      const direccion = config?.direccion || '';
      const telefono = config?.telefono || '';
      const email = config?.email || '';
      
      // Header con info del negocio
      doc.setFontSize(22);
      doc.setFont(undefined, 'bold');
      doc.text(nombreNegocio.toUpperCase(), 105, 20, { align: 'center' });
      
      doc.setFontSize(9);
      doc.setFont(undefined, 'normal');
      let headerY = 27;
      if (direccion) {
        doc.text(direccion, 105, headerY, { align: 'center' });
        headerY += 4;
      }
      if (telefono) {
        doc.text(`Tel: ${telefono}`, 105, headerY, { align: 'center' });
        headerY += 4;
      }
      if (email) {
        doc.text(email, 105, headerY, { align: 'center' });
      }
      
      // Título COTIZACIÓN
      doc.setFontSize(16);
      doc.setFont(undefined, 'bold');
      doc.text('COTIZACIÓN', 105, 45, { align: 'center' });
      
      // Línea separadora
      doc.setDrawColor(41, 128, 185);
      doc.setLineWidth(0.5);
      doc.line(20, 48, 190, 48);
      
      // Información de cotización (dos columnas)
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      let yPos = 56;
      
      // Columna izquierda
      doc.setFont(undefined, 'bold');
      doc.text('Folio:', 20, yPos);
      doc.setFont(undefined, 'normal');
      doc.text(cotizacion.folio, 45, yPos);
      
      yPos += 7;
      doc.setFont(undefined, 'bold');
      doc.text('Fecha:', 20, yPos);
      doc.setFont(undefined, 'normal');
      doc.text(new Date(cotizacion.fecha).toLocaleDateString('es-GT', { 
        year: 'numeric', month: 'long', day: 'numeric' 
      }), 45, yPos);
      
      yPos += 7;
      doc.setFont(undefined, 'bold');
      doc.text('Vigencia:', 20, yPos);
      doc.setFont(undefined, 'normal');
      doc.text(new Date(cotizacion.vigencia).toLocaleDateString('es-GT', { 
        year: 'numeric', month: 'long', day: 'numeric' 
      }), 45, yPos);
      
      // Columna derecha (Cliente)
      yPos = 56;
      doc.setFont(undefined, 'bold');
      doc.text('Cliente:', 110, yPos);
      doc.setFont(undefined, 'normal');
      doc.text(cotizacion.cliente_nombre || 'Público General', 135, yPos);
      
      if (cotizacion.cliente_telefono) {
        yPos += 7;
        doc.setFont(undefined, 'bold');
        doc.text('Teléfono:', 110, yPos);
        doc.setFont(undefined, 'normal');
        doc.text(cotizacion.cliente_telefono, 135, yPos);
      }
      
      yPos = 77;
      
      // Tabla de productos
      if (cotizacion.items && cotizacion.items.length > 0) {
        const tableData = cotizacion.items.map((item, index) => {
          const subtotal = (item.cantidad * item.precio_unitario) - (item.descuento || 0);
          const nombre = item.producto_nombre || item.nombre || 'Producto';
          return [
            index + 1,
            item.cantidad,
            nombre,
            formatCurrency(item.precio_unitario),
            item.descuento > 0 ? formatCurrency(item.descuento) : '-',
            formatCurrency(subtotal)
          ];
        });

        doc.autoTable({
          startY: yPos,
          head: [['#', 'Cant', 'Producto', 'Precio', 'Desc.', 'Subtotal']],
          body: tableData,
          theme: 'striped',
          headStyles: { 
            fillColor: [41, 128, 185], 
            fontStyle: 'bold',
            halign: 'center'
          },
          columnStyles: {
            0: { cellWidth: 10, halign: 'center' },
            1: { cellWidth: 15, halign: 'center' },
            3: { halign: 'right' },
            4: { halign: 'right' },
            5: { halign: 'right', fontStyle: 'bold' }
          },
          styles: {
            fontSize: 9,
            cellPadding: 3
          }
        });
        yPos = doc.lastAutoTable.finalY + 10;
      }
      
      // Total
      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.text(`TOTAL: ${formatCurrency(cotizacion.total)}`, 200, yPos, { align: 'right' });
      
      // Footer
      doc.setFontSize(8);
      doc.setFont(undefined, 'italic');
      doc.setTextColor(100, 100, 100);
      doc.text('Esta cotización tiene validez hasta la fecha de vigencia indicada', 105, 275, { align: 'center' });
      doc.text('Gracias por su preferencia', 105, 280, { align: 'center' });
      
      // Descargar
      const fileName = `cotizacion_${cotizacion.folio.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`;
      doc.save(fileName);
      
      alert('✅ PDF descargado correctamente');
    } catch (error) {
      console.error('Error al generar PDF:', error);
      alert('❌ Error al generar el PDF');
    }
  };

  const cotizacionesFiltradas = cotizaciones.filter(c =>
    c.folio?.toLowerCase().includes(filtro.toLowerCase()) ||
    c.cliente_nombre?.toLowerCase().includes(filtro.toLowerCase())
  );

  const getEstadoBadge = (estado) => {
    const badges = {
      pendiente: 'bg-yellow-100 text-yellow-800',
      aprobada: 'bg-green-100 text-green-800',
      rechazada: 'bg-red-100 text-red-800',
      vencida: 'bg-blue-100 text-blue-800'
    };
    return badges[estado] || badges.pendiente;
  };

  const formatEstado = (estado) => {
    const estados = {
      pendiente: 'Pendiente',
      aprobada: 'Aprobada',
      rechazada: 'Rechazada',
      vencida: 'Convertida'
    };
    return estados[estado] || estado;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Cotizaciones</h1>
          <p className="text-gray-600 mt-1">Gestión de cotizaciones y presupuestos</p>
        </div>
        <button
          onClick={crearCotizacion}
          className="btn-primary flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Nueva Cotización
        </button>
      </div>

      {/* Estadísticas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Cotizaciones</p>
              <p className="text-2xl font-bold text-gray-900">{cotizaciones.length}</p>
            </div>
            <FileText className="w-8 h-8 text-blue-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Pendientes</p>
              <p className="text-2xl font-bold text-yellow-600">
                {cotizaciones.filter(c => c.estado === 'pendiente').length}
              </p>
            </div>
            <FileText className="w-8 h-8 text-yellow-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Aprobadas</p>
              <p className="text-2xl font-bold text-green-600">
                {cotizaciones.filter(c => c.estado === 'aprobada').length}
              </p>
            </div>
            <Check className="w-8 h-8 text-green-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Convertidas</p>
              <p className="text-2xl font-bold text-blue-600">
                {cotizaciones.filter(c => c.estado === 'vencida').length}
              </p>
            </div>
            <ShoppingCart className="w-8 h-8 text-blue-500" />
          </div>
        </div>
      </div>

      {/* Buscador */}
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
            placeholder="Buscar por folio o cliente..."
            className="input-field pl-10"
          />
        </div>
      </div>

      {/* Tabla de cotizaciones */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Folio
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Fecha
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cliente
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Vigencia
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {cotizacionesFiltradas.map((cotizacion) => (
                <tr key={cotizacion.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="font-medium text-gray-900">{cotizacion.folio}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {new Date(cotizacion.fecha).toLocaleDateString('es-MX')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {cotizacion.cliente_nombre || 'Público General'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {formatCurrency(cotizacion.total)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {new Date(cotizacion.vigencia).toLocaleDateString('es-MX')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getEstadoBadge(cotizacion.estado)}`}>
                      {formatEstado(cotizacion.estado)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-2">
                      {/* Botones para cotizaciones pendientes */}
                      {cotizacion.estado === 'pendiente' && (
                        <>
                          <button
                            onClick={() => aprobarCotizacion(cotizacion.id)}
                            className="p-1.5 text-white bg-green-600 hover:bg-green-700 rounded-md transition-colors"
                            title="Aprobar Cotización"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => rechazarCotizacion(cotizacion.id)}
                            className="p-1.5 text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors"
                            title="Rechazar Cotización"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </>
                      )}
                      
                      {/* Botón WhatsApp */}
                      <button
                        onClick={() => compartirWhatsApp(cotizacion.id)}
                        className="text-green-600 hover:text-green-900"
                        title="Enviar por WhatsApp"
                      >
                        <MessageCircle className="w-5 h-5" />
                      </button>
                      
                      {/* Botón PDF */}
                      <button
                        onClick={() => descargarPDF(cotizacion.id)}
                        className="text-blue-600 hover:text-blue-900"
                        title="Descargar PDF"
                      >
                        <Download className="w-5 h-5" />
                      </button>
                      
                      {/* Botón Convertir a Venta */}
                      <button
                        onClick={() => convertirAVenta(cotizacion.id)}
                        disabled={cotizacion.estado === 'rechazada' || cotizacion.estado === 'vencida'}
                        className={`${
                          cotizacion.estado !== 'rechazada' && cotizacion.estado !== 'vencida'
                            ? 'text-purple-600 hover:text-purple-900 cursor-pointer'
                            : 'text-gray-400 cursor-not-allowed'
                        }`}
                        title={
                          cotizacion.estado !== 'rechazada' && cotizacion.estado !== 'vencida'
                            ? 'Convertir a Venta'
                            : 'No se pueden convertir cotizaciones rechazadas o vencidas'
                        }
                      >
                        <ShoppingCart className="w-5 h-5" />
                      </button>
                      
                      {/* Botón Eliminar */}
                      <button
                        onClick={() => eliminarCotizacion(cotizacion.id)}
                        className="text-red-600 hover:text-red-900"
                        title="Eliminar"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {cotizacionesFiltradas.length === 0 && (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No se encontraron cotizaciones</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CotizacionesPage;
